import { EventEmitter, Injectable } from '@angular/core';
import { Auth, browserLocalPersistence, reauthenticateWithCredential, createUserWithEmailAndPassword, EmailAuthCredential, EmailAuthProvider, IdTokenResult, linkWithCredential, signInAnonymously, signInWithEmailAndPassword, user, sendEmailVerification, FacebookAuthProvider, GoogleAuthProvider, TwitterAuthProvider, signInWithPopup, Unsubscribe, sendPasswordResetEmail } from '@angular/fire/auth';
import { Messaging, getToken } from '@angular/fire/messaging';
import { ModalController } from '@ionic/angular';
import { Subject, Subscription, Unsubscribable } from 'rxjs';
import { getFunctions, httpsCallable } from 'firebase/functions'
import { StorageService } from './storage.service';
import { AccountService } from '../account/account.service';
import { ObservableService } from './observable.service';
import { MessageService } from './message.service';

export interface SubscriptionTrait {
  id: string;
  companiesLeft: number;
  customersLeft: number;
  productsLeft: number;
  invoicesLeft: number;
  companiesLimit: number;
  customersLimit: number;
  productsLimit: number;
  invoicesLimit: number;
  version: string;
  refreshDate: number;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  static instance: AuthService;

  public get subscription() {
    return this._subscription
  }
  private _subscription: SubscriptionTrait;
  private logged: boolean;
  public get isLogged() {
    return !!this.currentUser && !this.currentUser.isAnonymous;
  }

  public pushToken: string;

  private _isLoading: boolean;
  
  public isLoginModalOpen: boolean;

  public code: string;

  public readonly onLocalWrite: EventEmitter<void> = new EventEmitter<void>();
  public readonly onWrite: EventEmitter<void> = new EventEmitter<void>();

  public _hasAFreeInvoice: boolean = true;
  public set hasAFreeInvoice(value: boolean) {
    this._hasAFreeInvoice = value;
    this.onLocalWrite.emit();
  }

  public get hasAFreeInvoice() {
    return this._hasAFreeInvoice;
  }

  public get isLoading() {
    return this._isLoading;
  }

  public get hasSubscription() {
    return this._subscription?.id != null;
  }

  // TODO
  public get canRead() {
    // return this.isLogged;
    return true;
  }

  public async canWrite(reference?: string, offset: number = 0) {
    // return this.isLogged 
    return true;
  }

  public async canLocalWrite(reference?: string, offset: number = 0) {
    // console.log(this._subscription[`${reference}Left`], this._subscription, this._subscription[`${reference}Left`] - offset, reference, !this._subscription[`${reference}Left`], this._subscription[`${reference}Left`] - offset >= 0)
    // return !this._subscription[`${reference}Left`] || this._subscription[`${reference}Left`] - offset >= 0
    return reference == 'invoices' && this._hasAFreeInvoice || reference != 'invoices'
  }

  public doWrite(reference: string, amount: number = 1) {
    // if (this.subscription[`${reference}Left`]) {
    //   this._subscription[`${reference}Left`] = Math.max(0, this._subscription[`${reference}Left`] - amount);
    //   if (!this.isLogged) return;
    //   this.currentUser.getIdTokenResult(true).then((value) => {
    //     this.updateTokenData(value);
    //   });
    // }
  }

  public isVersionUpdated(version: string = this.storageService.version) {
    return this._subscription?.version >= version;
  }

  private onAuthStateChanged = new Subject<void>();
  onAuthStateChanged$ = this.onAuthStateChanged.asObservable();
  
  private alreadyStarted: boolean = false;

  private displayName: string;

  private checkoutSub: Subscription;
  private authStateChangedSub: Unsubscribe;

  private passwordResetExpirationAt: number = 0;

  public get passwordResetExpirationIn() {
    return Math.max(this.passwordResetExpirationAt - Date.now(), 0);
  }

  constructor(
    private auth: Auth,
    private modal: ModalController,
    private storageService: StorageService,
    private messaging: Messaging,
    private account: AccountService,
    private observableService: ObservableService,
    private messageService: MessageService
  ) {
    const args = window.location.search.substring(1)?.split("&")?.map((arg) => arg.split("=")) || [];
    for (const arg of args) {
      if (arg[0] == 'code') {
        this.code = arg[1];
      }
    }
   }

  public async init() {
    return new Promise(async (resolve) => {
      AuthService.instance = this;
      
      await this.auth.setPersistence(browserLocalPersistence);
      
      this.logged = !!this.currentUser;

      if (this.authStateChangedSub) {
        this.authStateChangedSub();
      }

      this.authStateChangedSub = this.auth.onAuthStateChanged(async (user) => {
        
        this.logged = !!user && !user?.isAnonymous;
        if (this.logged) {
          
          await this.account.init({
            id: user.uid,
            name: this.displayName || user.displayName,
            email: user.email
          });
          const token = await this.currentUser?.getIdTokenResult(true);
          console.log("token:", token)
          this.updateTokenData(token);
        }
        
     
        // try {
        //   this.pushToken = await getToken(this.messaging);
        // } catch (e) {
        //   this.pushToken = "test-token";
        //   console.log(e)
        // }
        this.onAuthStateChanged.next();
        this.alreadyStarted = true;
        resolve(true);
      })

      if (this.checkoutSub) {
        this.checkoutSub.unsubscribe();
      }
      this.checkoutSub = this.observableService.onCheckout$.subscribe(async (id) => {
        this.refreshToken();
      });

    });
  }

  checkAuthStatus(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.alreadyStarted) {
        resolve(this.isLogged);
        return;
      }
      const subscription = this.onAuthStateChanged$.subscribe(() => {
        subscription.unsubscribe(); // Unsubscribe once we get the user data
        resolve(this.isLogged); // Resolve true if user is logged in, false otherwise
      }, reject);
    });
  }

  public async refreshToken() {
    const token = await this.currentUser?.getIdTokenResult(true);
    console.log("token:", token)
    this.updateTokenData(token);
  }

  public updateTokenData(token: IdTokenResult) {
    // this._subscription = token?.claims?.subscription || {
    //   id: null,
    //   companiesLeft: 0,
    //   customersLeft: 0,
    //   productsLeft: 0,
    //   invoicesLeft: 1,
    //   companiesLimit: 0,
    //   customersLimit: 0,
    //   productsLimit: 0,
    //   invoicesLimit: 0,
    //   version: null,
    //   refreshDate: null
    // };

    const paymentMethod = {
      type: token?.claims?.subscription?.paymentMethod,
      last4: null,
      brand: null
    }

    if (paymentMethod.type?.indexOf('_') > -1) {
      const paymentCard = paymentMethod.type.split('_');
      paymentMethod.type = 'card';
      paymentMethod.last4 = paymentCard[1];
      paymentMethod.brand = paymentCard[0];
    }
   
    this.account.subscription = {
      ...(token?.claims?.subscription || {}),
      paymentMethod,
      data: {
        maxClients: token?.claims?.subscription?.customersLimit,
        maxAppointments: token?.claims?.subscription?.invoicesLimit,
        currentClients: token?.claims?.clients,
        currentAppointments: token?.claims?.appointmentsTotal,
        maxWhatsappNotifications: token?.claims?.subscription?.maxWhatsappNotifications || 20,
        currentWhatsappNotifications: token?.claims?.subscription?.currentWhatsappNotifications || 0
      }
    };
    this.account.alreadyTrial = token?.claims?.alreadyTrial;
  }

  public get currentUser() {

    return this.auth.currentUser;
  }


  public async create({email, password, name}:{
    email: string; 
    password: string;
    name?: string;
  }) {
    this._isLoading = true;
    try {

      // const credential = EmailAuthProvider.credentialWithLink(email, password);
      // const credential = await createUserWithEmailAndPassword(this.auth, email, password);

      // Anonymously
      // const credential  = EmailAuthProvider.credential(email, password);
      // const userCredential = await linkWithCredential(this.currentUser, credential);
      // return await reauthenticateWithCredential(this.currentUser, credential);
      this.alreadyStarted = false;
      this.displayName = name;
      return await createUserWithEmailAndPassword(this.auth, email, password);

    } finally {
      this._isLoading = false;
    }
  }

  public async createWithCode({email, password, name, code: _code}:{
    email: string; 
    password: string;
    name?: string;
    code?: string;
  }) {
    this._isLoading = true;
    try {

      // const credential = EmailAuthProvider.credentialWithLink(email, password);
      // const credential = await createUserWithEmailAndPassword(this.auth, email, password);

      // Anonymously
      // const credential  = EmailAuthProvider.credential(email, password);
      // const userCredential = await linkWithCredential(this.currentUser, credential);
      // return await reauthenticateWithCredential(this.currentUser, credential);
      this.alreadyStarted = false;
      this.displayName = name;
      // return await createUserWithEmailAndPassword(this.auth, email, password);
      console.log("code:", _code, this.code)
      const code = _code || this.code;
      const functions = getFunctions();
      const createAccount = httpsCallable(functions, 'createaccount');
      const data: { passed: boolean; data?: any; message?: string; } = (await createAccount({
        email,
        password,
        name,
        code
      })).data as any;

      if (data.passed) {
        return this.login(email, password);
      } else {
        throw new Error(data?.message || "Error");
      }

    } finally {
      this._isLoading = false;
    }
  }


  public async createFB(accessToken: string) {
    this._isLoading = true;
    try {
      
      const credential = FacebookAuthProvider.credential(accessToken)
      // const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
    
      const userCredential = await linkWithCredential(this.currentUser, credential);
      // sendEmailVerification()
      return await reauthenticateWithCredential(this.currentUser, credential);
    } finally {
      this._isLoading = false;
    }
  }  

  public async createGoogle(idToken: string) {
    this._isLoading = true;
    try {
      // const credential =  await signInWithPopup(this.auth, new GoogleAuthProvider())
      
      const credential = GoogleAuthProvider.credential(idToken)
      // const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
    
      const userCredential = await linkWithCredential(this.currentUser, credential);
      // sendEmailVerification()
      return await reauthenticateWithCredential(this.currentUser, credential);
    } finally {
      this._isLoading = false;
    }
  } 

  public async createTwitter(token: string, secret: string) {
    this._isLoading = true;
    try {
      
      const credential = TwitterAuthProvider.credential(token, secret)
      // const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
    
      const userCredential = await linkWithCredential(this.currentUser, credential);
      // sendEmailVerification()
      return await reauthenticateWithCredential(this.currentUser, credential);
    } finally {
      this._isLoading = false;
    }
  } 

  public async login(email: string, password: string) {
    this._isLoading = true;
    try {
      return await signInWithEmailAndPassword(this.auth, email, password);
    } catch(e) {
      this.messageService.showToast(`COMMON.login.${e.code}`);
    } finally {
      this._isLoading = false;
    }
  }

  public async signout() {
    return this.auth.signOut()
  }

  public async showLoginModal() {
    // return new Promise(async resolve => {
    //   const modal = await this.modal.create({
    //     component: AccountSigninPage,
    //     componentProps: {
    //       onClose: async () => await modal.dismiss()
    //     },
    //   })

    //   await modal.present();
    //   await modal.onDidDismiss();
    //   resolve(true);
    // });
  }

  public async anonymousLogin() {
    return signInAnonymously(this.auth);
  }

  public async sendPasswordResetEmail(email: string) {
    if (this.passwordResetExpirationIn > 0) {
      return false;
    }
    const functions = getFunctions();
    
    const passwordReset = httpsCallable(functions, 'passwordReset');

    const data = (await passwordReset({ email }))?.data as any;

    if (data.passed) {
      this.passwordResetExpirationAt = Date.now() + 60000;
    } 
    return !!data.passed
  }
}
